import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import {
  KitchenBoardQueryType,
  KitchenFinishRequestType,
  KitchenManualInputRequestType,
  KitchenRejectRequestType,
  KitchenResetRequestType,
  KitchenScanRequestType,
  KitchenTransitionRequestType,
} from 'features/Order/@types/KitchenMonitoring'
import {
  KitchenCardApiType,
  KitchenDetailApiType,
  KitchenKanbanApiType,
  KitchenResultApiType,
  KitchenScanApiType,
  KitchenAdminFulfillRequestType,
  KitchenAdminUserApiType,
} from 'features/Order/@types/KitchenMonitoringApi'
import { kitchenApiStatus, mapKitchenBoard, mapKitchenDetail } from './kitchenMonitoringMapper'
import { kitchenMonitoringService as dummyService } from './kitchenMonitoringDummy'

// Live APIs by default. Dummy data requires an explicit development-only opt-in.
export const isKitchenDummyPreview =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_KITCHEN_MONITORING_DUMMY === 'true'

export const isKitchenRequestMock =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_KSP_REQUEST_MOCK === 'true'
const mockOptions = isKitchenRequestMock
  ? {
      config: {
        adapter: async (config: Parameters<import('axios').AxiosAdapter>[0]) => {
          const mock = await import('./kitchenMonitoringRequestMock')
          return mock.kitchenRequestMockAdapter(config)
        },
      },
    }
  : {}

const base = '/fulfillment/internal/v1/kitchen'
const cards = new Map<string, KitchenCardApiType>()
const scans = new Map<string, Map<string, KitchenScanApiType>>()
const key = (locationId: string, orderId: string) => `${locationId}:${orderId}`
const path = (value: string) => encodeURIComponent(value)
const post = async <T>(url: string, body?: object) => {
  const response = await axiosInstanceApiGateway.Post<T>({
    url: `${base}${url}`,
    data: body,
    ...mockOptions,
  })
  return response.data.data
}
const loadBoard = async (locationId: string) => {
  const response = await axiosInstanceApiGateway.Get<KitchenKanbanApiType>({
    url: `${base}/checker/kanban`,
    params: { location_id: locationId },
    ...mockOptions,
  })
  const board = response.data.data
  cards.forEach((_card, cacheKey) => {
    if (cacheKey.startsWith(`${locationId}:`)) cards.delete(cacheKey)
  })
  ;[...(board.incoming || []), ...(board.processing || []), ...(board.checking || [])].forEach(
    (card) => cards.set(key(locationId, card.order_id), card),
  )
  return board
}
const getCard = async (locationId: string, orderId: string) => {
  if (!cards.has(key(locationId, orderId))) await loadBoard(locationId)
  const card = cards.get(key(locationId, orderId))
  if (!card) throw new Error('Order Kitchen tidak ditemukan di hub ini')
  return card
}
const detail = async (locationId: string, orderId: string) => {
  const card = await getCard(locationId, orderId)
  const response = await axiosInstanceApiGateway.Get<{ order: KitchenDetailApiType }>({
    url: `${base}/orders/${path(orderId)}`,
    ...mockOptions,
  })
  return mapKitchenDetail(
    response.data.data.order,
    card,
    locationId,
    scans.get(key(locationId, orderId)) || new Map(),
  )
}
const applyScan = async (
  locationId: string,
  orderId: string,
  itemId: string,
  action: 'SCAN' | 'ACCEPT' | 'REJECT',
  qty: number,
  rejectionReason?: string,
) => {
  const card = await getCard(locationId, orderId)
  const order = await detail(locationId, orderId)
  if (!order.items.some(({ id }) => id === itemId))
    throw new Error('Item ID tidak ditemukan pada order ini')
  const result = await post<KitchenScanApiType>('/checker/scan', {
    line_item_alt_id: itemId,
    action: `SCAN_ACTION_${action}`,
    qty,
    ...(rejectionReason ? { rejection_reason: rejectionReason } : {}),
  })
  const cacheKey = key(locationId, orderId)
  const values = scans.get(cacheKey) || new Map<string, KitchenScanApiType>()
  values.set(result.line_item_alt_id, result)
  scans.set(cacheKey, values)
  card.ksp_status = result.ksp_status
  return {
    ...order,
    status: kitchenApiStatus(result.ksp_status),
    items: order.items.map((item) =>
      item.id === result.line_item_alt_id
        ? {
            ...item,
            progressKnown: true,
            processedQuantity: result.crew_scanned_count,
            checkedQuantity: result.checker_accepted_count,
          }
        : item,
    ),
  }
}
const validateResult = (
  results: KitchenResultApiType[],
  fulfillmentId: string,
  expected: string,
) => {
  const result = results.find(({ fulfillment_id }) => fulfillment_id === fulfillmentId)
  if (!result || result.error)
    throw new Error(result?.error || 'Backend tidak mengembalikan hasil order')
  if (result.incomplete_items?.length)
    throw new Error(
      `Item belum lengkap: ${result.incomplete_items
        .map((item) => `${item.product_name} (${item.missing_qty})`)
        .join(', ')}`,
    )
  if (result.ksp_status !== `KSP_ORDER_STATUS_${expected}`)
    throw new Error('Status order belum berubah. Muat ulang untuk memeriksa progres.')
  return result
}

export const kitchenMonitoringApiService = {
  async getBoard({ locationId, search }: KitchenBoardQueryType) {
    return mapKitchenBoard(await loadBoard(locationId), locationId, search)
  },
  async getDetail(locationId: string, orderId: string) {
    await loadBoard(locationId)
    return detail(locationId, orderId)
  },
  async transition({ locationId, orderId, targetStatus }: KitchenTransitionRequestType) {
    const card = await getCard(locationId, orderId)
    const order = await detail(locationId, orderId)
    if (targetStatus !== 'PROCESSING' || card.ksp_status !== 'KSP_ORDER_STATUS_INCOMING')
      throw new Error('Gunakan detail order untuk menyelesaikan proses setiap section')
    const response = await post<{ results: KitchenResultApiType[] }>('/checker/orders/start', {
      fulfillment_ids: [card.fulfillment_id],
    })
    card.ksp_status = validateResult(response.results, card.fulfillment_id, 'PROCESSING').ksp_status
    return { ...order, status: kitchenApiStatus(card.ksp_status) }
  },
  async scan({ locationId, orderId, code, action }: KitchenScanRequestType) {
    return applyScan(
      locationId,
      orderId,
      code.trim().replace(/^kitchen-/, ''),
      action === 'PROCESS' ? 'SCAN' : 'ACCEPT',
      1,
    )
  },
  async manualInput({
    locationId,
    orderId,
    itemId,
    sku,
    quantity,
    reason,
  }: KitchenManualInputRequestType) {
    if (sku.trim() !== itemId || !reason.trim()) throw new Error('Periksa Item ID dan alasan')
    if (!Number.isInteger(quantity) || quantity < 1)
      throw new Error('Qty harus bilangan bulat positif')
    // Swagger has no scan_source/manual reason field; do not invent request fields.
    return applyScan(locationId, orderId, itemId, 'SCAN', quantity)
  },
  async rejectItem({
    locationId,
    orderId,
    itemId,
    sku,
    quantity,
    reason,
    options,
  }: KitchenRejectRequestType) {
    if (sku.trim() !== itemId) throw new Error('Item ID tidak sesuai')
    if (!Number.isInteger(quantity) || quantity < 1)
      throw new Error('Qty harus bilangan bulat positif')
    return applyScan(
      locationId,
      orderId,
      itemId,
      'REJECT',
      quantity,
      [reason, ...options].join(': '),
    )
  },
  async resetItem(_: KitchenResetRequestType) {
    throw new Error('Reset item belum tersedia di API Kitchen')
  },
  async finish({ locationId, orderId, action }: KitchenFinishRequestType) {
    const card = await getCard(locationId, orderId)
    const order = await detail(locationId, orderId)
    if (action === 'CHECK') {
      const response = await post<{ results: KitchenResultApiType[] }>('/checker/orders/complete', {
        fulfillment_ids: [card.fulfillment_id],
      })
      card.ksp_status = validateResult(response.results, card.fulfillment_id, 'COMPLETE').ksp_status
    } else {
      if (!card.sections?.length) throw new Error('Backend belum mengirim section order')
      await card.sections
        .filter(({ complete }) => !complete)
        .reduce(async (previous, section) => {
          await previous
          const result = await post<{ ksp_status: string }>(
            `/self/orders/${path(card.fulfillment_id)}/sections/${path(
              section.section_name,
            )}/complete`,
          )
          card.ksp_status = result.ksp_status
          card.sections = card.sections?.map((value) =>
            value.section_name === section.section_name ? { ...value, complete: true } : value,
          )
        }, Promise.resolve())
      if (card.ksp_status !== 'KSP_ORDER_STATUS_CHECKING')
        throw new Error('Order belum siap dicek. Section lain mungkin belum selesai.')
    }
    return { ...order, status: kitchenApiStatus(card.ksp_status) }
  },
  async partialFulfill(
    locationId: string,
    orderId: string,
    itemId: string,
    quantity: number,
    note: string,
    updatedBy?: KitchenAdminUserApiType,
  ) {
    const order = await detail(locationId, orderId)
    const item = order.items.find(({ id }) => id === itemId)
    if (!Number.isInteger(quantity) || quantity < 0 || !note.trim())
      throw new Error('Periksa Qty dan catatan partial fulfil')
    if (!item || quantity > item.quantity) throw new Error('Item ID atau Qty tidak sesuai')
    if (!updatedBy?.id || !updatedBy.name) throw new Error('Data user login tidak tersedia')
    const data: KitchenAdminFulfillRequestType = {
      items: [{ id: item.adminItemId || item.id, fulfill_quantity: quantity, note: note.trim() }],
      updated_by: updatedBy,
    }
    const response = await axiosInstanceApiGateway.Put<{ order: KitchenDetailApiType }>({
      url: `${base}/orders/${path(orderId)}/items`,
      data,
      ...mockOptions,
    })
    return response.data.data.order
  },
  async getPrinterStatus(locationId: string) {
    const response = await axiosInstanceApiGateway.Get<{
      sections: { section_name: string; status: string }[]
    }>({ url: `${base}/printer/status`, params: { location_id: locationId }, ...mockOptions })
    const sections = response.data.data.sections || []
    return {
      available:
        sections.length > 0 && sections.every(({ status }) => status === 'PRINTER_STATUS_ONLINE'),
      simulated: false,
      sections,
    }
  },
  async printLabel(
    _locationId: string,
    _orderId: string,
  ): Promise<{ queued: boolean; message: string }> {
    throw new Error('API hanya mencatat label tercetak; koneksi cetak printer belum tersedia')
  },
  async markPrinted(orderId: string, itemIds: string[]) {
    return post<{ marked_count: number }>(`/orders/${path(orderId)}/printed`, {
      line_item_alt_ids: itemIds,
    })
  },
}

export const kitchenMonitoringService =
  isKitchenDummyPreview && !isKitchenRequestMock
    ? { ...kitchenMonitoringApiService, ...dummyService }
    : kitchenMonitoringApiService
